import dayjs, { Dayjs } from "dayjs";
import create from "zustand";
import {
    DashboardDisplayOptions,
    DashboardFilter,
    DashboardGroupBy,
    DashboardMode,
    DashboardScope,
} from "@/types/Dashboard";

const queryParams = new URLSearchParams(location.search);

export type DateNavigationDirection = "prev" | "next" | "today";

const DEFAULT_FILTERS: DashboardFilter = {
    products: { all: true, selected: [] },
    guide: { showUnassigned: true, all: true, selected: [] },
    resource: { showUnassigned: true, all: true, selected: [] },
    scope: (queryParams.get("scope") as DashboardScope) ?? DashboardScope.RESERVED,
    mode: (queryParams.get("mode") as DashboardMode) ?? DashboardMode.LIST,
    groupBy: (queryParams.get("groupBy") as DashboardGroupBy) ?? DashboardGroupBy.NONE,
};

export const DEFAULT_DISPLAY_OPTIONS: DashboardDisplayOptions = {
    expandAllRows: false,
    showSummaryRows: false,
};
interface DashboardStoreState {
    date: Dayjs;
    endDate: Dayjs;
    previousDate: Dayjs | null;
    filters: DashboardFilter;
    displayOptions: DashboardDisplayOptions;
    viewId: number | "default" | null;
    isRealTimeUpdate: boolean;
    getMode: () => DashboardMode;
    setViewId: (viewId: number | "default" | null) => void;
    setFilters: (filters: Partial<DashboardFilter>) => void;
    resetFilters: (filters?: Partial<DashboardFilter>) => void;
    setDate: (date: Dayjs) => void;
    setEndDate: (date: Dayjs) => void;
    setIsRealTimeUpdate: (isRealTimeRefetch: boolean) => void;
    setDisplayOptions: (displayOptions: DashboardDisplayOptions) => void;
    resetDisplayOptions: () => void;
    changeDate: (direction: DateNavigationDirection) => void;
}

export const useDashboardFilterStore = create<DashboardStoreState>((set, get) => ({
    date: dayjs(), // Not using `dayjs.tz()` or `now()` since this is called statically and the seller data is not loaded at this point.
    // var for storing the date before we set the new date
    previousDate: null,
    filters: DEFAULT_FILTERS,
    viewId: null, // The custom view ID.
    isRealTimeUpdate: false,
    endDate: dayjs(),
    displayOptions: DEFAULT_DISPLAY_OPTIONS,

    getMode() {
        return get().filters.mode;
    },

    setFilters(filters: Partial<DashboardFilter>) {
        set((state) => ({ filters: { ...state.filters, ...filters } }));
    },

    resetFilters(filters?: Partial<DashboardFilter>) {
        set({ filters: { ...DEFAULT_FILTERS, ...filters } });
    },

    setDate: (newDate: Dayjs) => set((state) => ({ previousDate: state.date, date: newDate })),

    setEndDate: (newDate: Dayjs) => set(() => ({ endDate: newDate })),

    setViewId(viewId: number | "default" | null) {
        set({ viewId });
    },

    setIsRealTimeUpdate(isRealTimeUpdate: boolean) {
        set(() => ({ isRealTimeUpdate }));
    },

    setDisplayOptions(displayOptions: DashboardDisplayOptions) {
        set(() => ({ displayOptions }));
    },
    resetDisplayOptions() {
        set(() => ({ displayOptions: DEFAULT_DISPLAY_OPTIONS }));
    },

    changeDate: (direction: DateNavigationDirection) => {
        const currentDate = get().date;

        const newDate =
            direction === "prev"
                ? dayjs(currentDate).subtract(1, "day")
                : direction === "next"
                ? dayjs(currentDate).add(1, "day")
                : dayjs();

        get().setDate(newDate);
    },
}));

interface ModalState {
    isOpen: boolean;
    eventId: ID;
    setIsModalOpen: (isOpen: boolean, eventId: ID) => void;
}

export const useModalStore = create<ModalState>((set) => ({
    isOpen: false,
    eventId: "",
    setIsModalOpen: (isOpen, eventId) => set({ isOpen, eventId }),
}));

export const isListView = () => {
    return useDashboardFilterStore.getState().filters.mode === DashboardMode.LIST;
};

export const isTableView = () => {
    return useDashboardFilterStore.getState().filters.mode === DashboardMode.TABLE;
};

export const isDayView = () => {
    return [DashboardMode.LIST, DashboardMode.DAY, DashboardMode.EQUIPMENT, DashboardMode.TABLE].includes(
        useDashboardFilterStore.getState().filters.mode,
    );
};

export const isSingleDateView = () => {
    return [DashboardMode.DAY, DashboardMode.EQUIPMENT, DashboardMode.TABLE].includes(
        useDashboardFilterStore.getState().filters.mode,
    );
};

export const isCalendarView = () => {
    return [DashboardMode.WEEK, DashboardMode.DAY, DashboardMode.MONTH, DashboardMode.EQUIPMENT].includes(
        useDashboardFilterStore.getState().filters.mode,
    );
};

export const showDateNavButtons = () => {
    return [
        DashboardMode.WEEK,
        DashboardMode.DAY,
        DashboardMode.MONTH,
        DashboardMode.EQUIPMENT,
        DashboardMode.TABLE,
    ].includes(useDashboardFilterStore.getState().filters.mode);
};

export const isUngroupedTableView = () => {
    return useDashboardFilterStore.getState().filters.groupBy === DashboardGroupBy.NONE;
};

export const isUngroupedTablePrintView = (isPrint = false) => {
    return isPrint && useDashboardFilterStore.getState().filters.groupBy === DashboardGroupBy.NONE;
};

interface useDashboardRosterStore {
    selectedRosterView: number | string | null;
    setRosterView: (viewId: number | string | null) => void;
    // Equipment-based roster only: which sub-views are shown. Default both on; synced from the active view.
    showAttendeeView: boolean;
    showEquipmentView: boolean;
    setRosterSubViews: (subViews: { showAttendeeView: boolean; showEquipmentView: boolean }) => void;
}

export const useDashboardRosterStore = create<useDashboardRosterStore>((set) => ({
    selectedRosterView: null,
    setRosterView: (viewId) => set({ selectedRosterView: viewId }),
    showAttendeeView: true,
    showEquipmentView: true,
    setRosterSubViews: (subViews) => set(subViews),
}));
